import { useCallback, useMemo, useRef } from 'react';
import { toaster } from '@optiaxiom/react';
import { Query, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { extractApiErrorMessage } from '@frontend/api/http';
import { useInstanceId } from '@frontend/hooks';
import { useSocket, useSocketChannel } from '@frontend/hooks/useSocket';
import {
  WorkflowAgent,
  WorkflowAgentExecution,
  WorkflowAgentTriggerResponse,
  WorkflowSocketMessage,
} from '@frontend/types/workflow-agent';

import { useWorkflowPermissions } from '../components/workflow-agent/hooks/useWorkflowPermissions';
import {
  convertOrganicWorkflowToGraph,
  convertWorkflowToGraph,
  OrganicAgentInfo,
} from '../components/workflow-agent/services/workflow-to-graph';
import {
  commitTriggerChanges,
  createWorkflowAgent,
  deleteWorkflowAgent,
  getWorkflowAgent,
  getWorkflowAgentExecution,
  getWorkflowAgents,
  getWorkflowAgentUrl,
  triggerWorkflowAgent,
  updateWorkflowAgent,
} from '../services/workflow-agent';

import { useGetSpecializedAgentsList } from './useSpecializedAgents';

export function useGetWorkflowAgentsList(options?: { enabled?: boolean }) {
  const { instanceId } = useInstanceId();
  const {
    data: agents,
    isPending,
    error,
  } = useQuery<WorkflowAgent[]>({
    queryKey: getWorkflowAgents(instanceId ?? '').key(),
    queryFn: getWorkflowAgents(instanceId ?? '').queryFn(),
    enabled: !!instanceId && (options?.enabled ?? true),
  });

  return { agents: agents ?? [], isPending, error };
}

export function useGetWorkflowAgent(url: string, enabled: boolean) {
  const {
    data: agent,
    isPending,
    error,
  } = useQuery<WorkflowAgent>({
    queryKey: ['opal:workflow-agent', url],
    queryFn: getWorkflowAgent(url),
    enabled: !!url && enabled,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  return { agent: agent ?? undefined, isPending, error };
}

export function useGetWorkflowAgentWithGraph(
  url: string,
  enabled: boolean,
  options?: { legacyConditionRendering?: boolean }
) {
  const { agent, isPending, error } = useGetWorkflowAgent(url, enabled);

  // Get user permissions for trigger deletion
  const permissions = useWorkflowPermissions();

  // Organic agents render one specialized-agent node per agent_choice id; resolve the
  // display name/description from the instance's specialized-agent list.
  const { agents: specializedAgents } = useGetSpecializedAgentsList();
  const resolveAgent = useMemo(() => {
    const byId = new Map<string, OrganicAgentInfo>(
      specializedAgents.map(a => [
        a.id,
        {
          name: a.name,
          description: a.description,
          is_system_agent: a.is_system_agent,
          links: a.links,
        },
      ])
    );
    return (agentId: string): OrganicAgentInfo | undefined => byId.get(agentId);
  }, [specializedAgents]);

  // Extract boolean for stable useMemo dependency
  const legacyConditionRendering = options?.legacyConditionRendering ?? false;

  // Extract and validate metadata from agent_metadata field
  const metadata = useMemo(() => {
    if (!agent?.agent_metadata || typeof agent.agent_metadata !== 'object') {
      return { nodes: [], edges: [] };
    }

    // Type guard to check if metadata nodes are valid
    const isValidMetadataNode = (
      node: unknown
    ): node is {
      id: string;
      position?: { x: number; y: number };
      measured?: { width: number; height: number };
    } => {
      return (
        typeof node === 'object' &&
        node !== null &&
        'id' in node &&
        typeof (node as Record<string, unknown>).id === 'string'
      );
    };

    const rawNodes = agent.agent_metadata.nodes;
    if (!Array.isArray(rawNodes)) {
      console.warn('agent_metadata.nodes is not an array, using empty nodes');
      return { nodes: [], edges: [] };
    }

    // Validate and sanitize metadata nodes
    const validatedNodes = rawNodes.filter(isValidMetadataNode).map(node => ({
      id: node.id,
      position: node.position || { x: 0, y: 0 },
      measured: node.measured,
    }));

    if (validatedNodes.length !== rawNodes.length) {
      console.warn(
        `Filtered out ${rawNodes.length - validatedNodes.length} invalid metadata nodes`
      );
    }

    return {
      nodes: validatedNodes,
      edges: Array.isArray(agent.agent_metadata.edges) ? agent.agent_metadata.edges : [],
    };
  }, [agent?.agent_metadata]);

  // Convert workflow data to graph format. Organic agents have no step graph: their
  // canvas is the triggers + an eligible specialized-agent node per agent_choice id.
  const { nodes, edges } = useMemo(
    () =>
      agent?.workflow_type === 'organic'
        ? convertOrganicWorkflowToGraph(
            agent?.agent_choice,
            agent?.triggers || [],
            metadata,
            resolveAgent,
            permissions
          )
        : convertWorkflowToGraph(
            agent?.steps || {},
            agent?.triggers || [],
            agent?.start_step_id || '',
            metadata,
            permissions,
            legacyConditionRendering ? { legacyConditionRendering: true } : undefined
          ),
    [
      agent?.workflow_type,
      agent?.agent_choice,
      agent?.steps,
      agent?.triggers,
      agent?.start_step_id,
      metadata,
      permissions,
      legacyConditionRendering,
      resolveAgent,
    ]
  );

  const agentWithGraph = useMemo(() => {
    if (!agent) return undefined;

    return {
      ...agent,
      nodes,
      edges,
    };
  }, [agent, nodes, edges]);

  return { agent: agentWithGraph, isPending, error };
}

export function useCreateWorkflowAgent(onSuccess?: (agent: WorkflowAgent) => void) {
  const queryClient = useQueryClient();
  const { instanceId } = useInstanceId();
  const { mutate, isPending, error } = useMutation({
    mutationFn: async (agent: WorkflowAgent) => {
      return createWorkflowAgent(agent, instanceId ?? '');
    },
    onSuccess: savedAgent => {
      queryClient.invalidateQueries({ queryKey: ['opal:workflow-agents'] });
      if (savedAgent.id) {
        queryClient.invalidateQueries({
          predicate: query =>
            Array.isArray(query.queryKey) &&
            query.queryKey.includes('opal:workflow-agent-versions') &&
            query.queryKey.includes(savedAgent.id),
        });
      }
      toaster.create('Workflow agent created successfully', { intent: 'success' });
      onSuccess?.(savedAgent);
    },
    onError: (error: unknown) => {
      toaster.create(extractApiErrorMessage(error), { intent: 'danger' });
    },
  });
  return { mutate, isPending, error };
}

export function useUpdateWorkflowAgent(onSuccess?: (agent: WorkflowAgent) => void) {
  const queryClient = useQueryClient();
  const { instanceId } = useInstanceId();
  const { mutate, isPending, error } = useMutation({
    mutationFn: async ({
      updatedAgent,
      updateUrl,
      triggerNodes,
      pendingTriggerDeletions,
    }: {
      updatedAgent: WorkflowAgent;
      updateUrl: string;
      triggerNodes?: Array<{ data: Record<string, unknown>; type?: string; id?: string }>;
      pendingTriggerDeletions?: Array<{ id: string; selfUrl: string; type: string }>;
    }) => {
      let triggerResult:
        | { succeeded: number; failed: number; idRemap: Record<string, string> }
        | undefined;
      if (
        (triggerNodes && triggerNodes.length > 0) ||
        (pendingTriggerDeletions && pendingTriggerDeletions.length > 0)
      ) {
        const agentId = updatedAgent.id || '';
        triggerResult = await commitTriggerChanges(
          instanceId ?? '',
          agentId,
          triggerNodes || [],
          pendingTriggerDeletions || []
        );
      }

      let agentForPut = updatedAgent;
      if (triggerResult?.idRemap && Object.keys(triggerResult.idRemap).length > 0) {
        const remap = triggerResult.idRemap;
        const remappedNodes = (updatedAgent.nodes || []).map(node =>
          remap[node.id]
            ? { ...node, id: remap[node.id], data: { ...node.data, id: remap[node.id] } }
            : node
        );
        const remappedEdges = (updatedAgent.edges || []).map(edge => ({
          ...edge,
          source: remap[edge.source] || edge.source,
          target: remap[edge.target] || edge.target,
        }));
        agentForPut = {
          ...updatedAgent,
          nodes: remappedNodes as WorkflowAgent['nodes'],
          edges: remappedEdges as WorkflowAgent['edges'],
        };
      }

      const agent = await updateWorkflowAgent(agentForPut, updateUrl);
      return { agent, triggerResult };
    },

    onSuccess: ({ agent: updatedAgent, triggerResult }) => {
      queryClient.invalidateQueries({ queryKey: ['opal:workflow-agents'] });
      queryClient.invalidateQueries({
        queryKey: ['opal:workflow-agent', getWorkflowAgentUrl(instanceId ?? '', updatedAgent.id)],
      });
      queryClient.invalidateQueries({
        predicate: query =>
          Array.isArray(query.queryKey) &&
          query.queryKey.includes('opal:workflow-agent-versions') &&
          query.queryKey.includes(updatedAgent.id),
      });
      if (triggerResult?.failed) {
        toaster.create(
          `Workflow agent updated. ${triggerResult.failed} trigger(s) could not be saved.`,
          { intent: 'warning' }
        );
      } else {
        toaster.create('Workflow agent updated successfully', { intent: 'success' });
      }
      onSuccess?.(updatedAgent);
    },
    onError: (error: unknown) => {
      toaster.create(extractApiErrorMessage(error), { intent: 'danger' });
    },
  });
  return { mutate, isPending, error };
}

export function useDeleteWorkflowAgent(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  const silentRef = useRef(false);
  const { mutate, mutateAsync, isPending, error } = useMutation({
    mutationFn: async (deleteUrl: string) => {
      return deleteWorkflowAgent(deleteUrl);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opal:workflow-agents'] });
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      if (!silentRef.current)
        toaster.create('Workflow agent deleted successfully', { intent: 'success' });
      onSuccess?.();
    },
    onError: (error: unknown) => {
      if (!silentRef.current) toaster.create(extractApiErrorMessage(error), { intent: 'danger' });
    },
  });
  return { mutate, mutateAsync, isPending, error, silentRef };
}

export function useTriggerWorkflowAgent(onSuccess?: (data: WorkflowAgentTriggerResponse) => void) {
  const { instanceId } = useInstanceId();
  const { mutate, isPending, error } = useMutation({
    mutationFn: async ({ agentId, inputText }: { agentId: string; inputText: string }) => {
      return triggerWorkflowAgent(instanceId ?? '', agentId, inputText);
    },
    onSuccess: data => {
      toaster.create('Workflow agent triggered successfully', { intent: 'success' });
      onSuccess?.(data);
    },
    onError: (error: unknown) => {
      toaster.create(extractApiErrorMessage(error), { intent: 'danger' });
    },
  });
  return { mutate, isPending, error };
}

export function useGetWorkflowAgentExecution(executionId: string | undefined) {
  const { instanceId } = useInstanceId();
  const queryClient = useQueryClient();
  const { connected: socketConnected } = useSocket();

  const { data, isFetching, error } = useQuery<WorkflowAgentExecution>({
    queryKey: ['opal:workflow-agent-execution', executionId],
    queryFn: () => getWorkflowAgentExecution(instanceId ?? '', executionId || ''),
    enabled: !!executionId && !!instanceId,
    refetchOnWindowFocus: false,
    refetchInterval: (query: Query<WorkflowAgentExecution, Error>) => {
      const status = query.state?.data?.status;
      const isActive = status === 'pending' || status === 'running';
      return !socketConnected && isActive ? 3000 : false;
    },
  });

  const handleSocketUpdate = useCallback(
    (msg: WorkflowSocketMessage) => {
      if (msg.type === 'started' || msg.type === 'completion') {
        queryClient.invalidateQueries({ queryKey: ['opal:workflow-agent-execution', executionId] });
      }
    },
    [queryClient, executionId]
  );

  useSocketChannel(executionId ? `workflow_execution:${executionId}` : null, handleSocketUpdate);

  return { data, isFetching, error };
}
